import { useCallback, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { cn } from 'components/UI/cn'

const lineClampClass: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
  5: 'line-clamp-5',
  6: 'line-clamp-6',
}

export function OverflownText({
  children,
  maxLines = 1,
  tooltipText,
  placement = 'top',
  className,
  ...restProps
}: {
  children: React.ReactNode
  maxLines?: number
  tooltipText?: string
  placement?: 'top' | 'right' | 'bottom' | 'left'
} & React.HTMLAttributes<HTMLDivElement>) {
  const ref = useRef<HTMLElement>(null)
  const [isOverflown, setIsOverflown] = useState(false)

  const checkOverflow = useCallback(() => {
    const element = ref.current
    if (!element) return

    const computedStyle = getComputedStyle(element)
    const lineHeight = parseFloat(computedStyle.lineHeight)

    const checkElement =
      element.children.length > 0 ? element.children[0] : element

    const heightOverflow = element.scrollHeight > lineHeight * maxLines
    const widthOverflow =
      (checkElement as HTMLElement).scrollWidth >
      (checkElement as HTMLElement).clientWidth
    const isOverflowing = maxLines === 1 ? widthOverflow : heightOverflow

    setIsOverflown(isOverflowing)
  }, [maxLines])

  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
        resizeObserverRef.current = null
      }
      ref.current = node
      if (node) {
        const observer = new ResizeObserver(checkOverflow)
        observer.observe(node)
        resizeObserverRef.current = observer
        checkOverflow()
      }
    },
    [checkOverflow]
  )

  const clampClass = lineClampClass[maxLines] || `line-clamp-[${maxLines}]`

  // Tooltip mode: show tooltip on overflow
  if (tooltipText !== undefined) {
    return (
      <div className="min-h-full flex items-center z-[1] w-full max-w-full min-w-0 hover:relative">
        <OverlayTrigger
          placement={placement}
          overlay={
            isOverflown ? (
              <Tooltip className="!fixed">{tooltipText}</Tooltip>
            ) : (
              <></>
            )
          }
        >
          <span
            ref={setRef as React.Ref<HTMLSpanElement>}
            className={cn(
              'overflow-hidden text-ellipsis inline-block w-full max-w-full min-w-0',
              maxLines === 1
                ? 'whitespace-nowrap'
                : [clampClass, 'whitespace-normal'],
              className
            )}
            {...restProps}
          >
            {children}
          </span>
        </OverlayTrigger>
      </div>
    )
  }

  // Hover-expand mode: expand text on hover
  return (
    <div className="min-h-full flex items-center z-[1] w-full max-w-full min-w-0 hover:relative">
      <div
        ref={setRef as React.Ref<HTMLDivElement>}
        className={cn(
          'overflow-hidden text-ellipsis w-auto max-w-full',
          clampClass,
          isOverflown && [
            'hover:absolute hover:top-0 hover:left-0 hover:overflow-visible',
            'hover:whitespace-normal hover:break-words hover:hyphens-auto',
            'hover:[-webkit-line-clamp:initial]',
            'hover:max-w-full hover:w-full hover:z-[1]',
            "hover:before:content-[''] hover:before:bg-surface-sunken",
            'hover:before:border hover:before:border-line',
            'hover:before:h-full hover:before:block hover:before:inset-0',
            'hover:before:w-full hover:before:rounded hover:before:absolute',
            'hover:before:-z-10',
          ],
          className
        )}
        {...restProps}
      >
        {children}
      </div>
    </div>
  )
}
